/**
 * DOM helpers for the notify-me modal. Nothing here runs at module load —
 * every function takes the document explicitly, keeping the SDK SSR-safe.
 * All classes are `stoq-` prefixed and styling lives in a single injected
 * <style> tag (plus per-element inline styles for merchant colors).
 */

export const STYLE_TAG_ID = 'stoq-sdk-modal-styles'

const CSS = `
.stoq-modal-overlay{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(33,34,35,.55);padding:16px;box-sizing:border-box}
.stoq-modal{position:relative;width:100%;max-width:420px;max-height:90vh;overflow-y:auto;padding:24px;box-shadow:0 10px 30px rgba(0,0,0,.25);box-sizing:border-box;-webkit-font-smoothing:antialiased}
.stoq-modal:focus{outline:none}
.stoq-inline-form{position:relative;width:100%;padding:20px;box-sizing:border-box;border:1px solid rgba(0,0,0,.08)}
.stoq-modal *,.stoq-inline-form *{box-sizing:border-box;margin:0}
.stoq-form-close{position:absolute;top:12px;right:12px;width:32px;height:32px;display:flex;align-items:center;justify-content:center;border:none;background:transparent;color:inherit;cursor:pointer;border-radius:4px;font-size:20px;line-height:1;padding:0}
.stoq-form-close:hover{background:rgba(0,0,0,.06)}
.stoq-form-image{width:100%;max-height:180px;object-fit:contain;border-radius:4px;margin-bottom:12px;display:block}
.stoq-form-header{font-size:1.25em;font-weight:600;line-height:1.3;margin-bottom:8px;padding-right:32px}
.stoq-form-description{font-size:.95em;line-height:1.45;margin-bottom:12px}
.stoq-form-product{font-size:.95em;font-weight:600;margin-bottom:16px}
.stoq-form-field{margin-bottom:12px}
.stoq-form-input{width:100%;padding:10px 12px;font:inherit;color:inherit;background:#fff;color:#202223;border:1px solid #dfe3e8;border-radius:6px}
.stoq-form-input:focus{outline:2px solid currentColor;outline-offset:1px}
.stoq-form-input--error{border-color:#ff3737;background:#fde2de}
.stoq-form-error{font-size:.9em;color:#d72c0d;margin:8px 0}
.stoq-form-error--duplicate{color:inherit;opacity:.85}
.stoq-form-button{width:100%;padding:12px 16px;font:inherit;font-weight:600;border:none;border-radius:6px;cursor:pointer;margin-top:4px}
.stoq-form-button[disabled]{opacity:.6;cursor:default}
.stoq-form-terms{font-size:.8em;line-height:1.4;opacity:.75;margin-top:12px}
.stoq-form-success{display:flex;align-items:flex-start;gap:8px;font-size:.95em;line-height:1.45;padding:8px 0}
.stoq-form-success-icon{flex:none;width:20px;height:20px}
.stoq-sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
`

/** Injects the SDK stylesheet once per document. */
export function injectStyles(doc: Document): void {
  if (doc.getElementById(STYLE_TAG_ID)) return
  const style = doc.createElement('style')
  style.id = STYLE_TAG_ID
  style.textContent = CSS
  doc.head.appendChild(style)
}

interface ElOptions {
  className?: string
  text?: string
  html?: string
  attrs?: Record<string, string>
}

/** Tiny createElement helper. */
export function el<K extends keyof HTMLElementTagNameMap>(
  doc: Document,
  tag: K,
  options: ElOptions = {},
): HTMLElementTagNameMap[K] {
  const node = doc.createElement(tag)
  if (options.className !== undefined) node.className = options.className
  if (options.text !== undefined) node.textContent = options.text
  if (options.html !== undefined) node.innerHTML = options.html
  if (options.attrs) {
    for (const [name, value] of Object.entries(options.attrs)) {
      node.setAttribute(name, value)
    }
  }
  return node
}

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Keydown handler implementing a Tab focus trap inside `root`.
 * Returns true when the event was handled.
 */
export function trapFocus(root: HTMLElement, event: KeyboardEvent): boolean {
  if (event.key !== 'Tab') return false
  const focusable = root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  if (focusable.length === 0) return false

  const first = focusable[0]!
  const last = focusable[focusable.length - 1]!
  const active = root.ownerDocument.activeElement

  if (event.shiftKey) {
    if (active === first || !root.contains(active)) {
      event.preventDefault()
      last.focus()
      return true
    }
  } else if (active === last || !root.contains(active)) {
    event.preventDefault()
    first.focus()
    return true
  }
  return false
}

/** Focuses the first form input inside `root` (falls back to `root`). */
export function focusFirstInput(root: HTMLElement): void {
  const input = root.querySelector<HTMLElement>(
    'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])',
  )
  ;(input ?? root).focus()
}
